import classnames from 'classnames'
import { useTranslation } from 'react-i18next'

import Badge from 'common/components/badge'
import Icons from 'common/components/icons'
import ViewerCounter from 'common/helpers/viewerCounter'
import { Status } from 'modules/transmission/types'
import { useChatContext } from 'modules/chat/context'
import useInitSparkie from 'modules/chat/hooks/useInitSparkie'
import { useTransmissionContext } from 'modules/transmission/context'
import { useLiveRequestWithRetry } from 'modules/transmission/hooks/useLiveRequestWithRetry'
import LanguageDropdown from 'modules/transmission/components/languageDropdown'

import './liveBadges.scss'
import '@hotmart/cosmos/dist/styles/utilities/flex/flex.css'
import '@hotmart/cosmos/dist/styles/utilities/display/display.css'
import '@hotmart/cosmos/dist/styles/utilities/spacing/spacing.css'
import '@hotmart/cosmos/dist/styles/utilities/text/variations/grays.css'
import '@hotmart/cosmos/dist/styles/utilities/background/variations/red.css'

export interface Props {
  liveStatus: Status
}

const LiveBadges = ({ liveStatus }: Props) => {
  const { t } = useTranslation()
  const {
    viewers,
    isChatActive,
    showChat,
    setShowChat,
    hasError,
    isLoading: isLoadingChat
  } = useChatContext()
  const { showOnfireLanguages } = useTransmissionContext()
  const liveRequest = useLiveRequestWithRetry()

  const { initSparkie } = useInitSparkie()

  const isCompleted = liveRequest.data?.status === Status.COMPLETED
  const isLoading = isLoadingChat || liveRequest.isLoading

  const renderShowChatBadge = () => {
    if (!(isLoading || (isChatActive && !isCompleted && !hasError))) {
      return null
    }

    let props = {
      onClick: () => setShowChat(!showChat),
      label: showChat ? 'chat.hide_chat' : 'chat.show_chat'
    }

    if (hasError) {
      props = {
        onClick: () => initSparkie(),
        label: 'general.try_again'
      }
    }

    return (
      <Badge
        className={classnames('_ml-4', {
          'active-badge': showChat && !isLoading
        })}
        onClick={props.onClick}
        dataTest="chat-visibility-button"
        isLoading={isLoading}>
        {t(props.label)}
        <Icons
          className="live-badges-icon _ml-1"
          iconName="comment-alt-lines"
          prefix="far"/>
      </Badge>
    )
  }

  const renderLeftBadges = () => {
    return (
      <div className="_d-flex">
        {liveStatus === Status.STARTED && (
          <Badge className="_bg-red _text-white _mr-4 red-badge-border">
            {t('general.live')}
          </Badge>
        )}
        {showOnfireLanguages && <LanguageDropdown />}
      </div>
    )
  }

  const renderRightBadges = () => {
    return (
      <div className="_d-flex">
        <Badge isLoading={isLoading}>
          <Icons
            className="live-badges-icon _mr-1"
            iconName="users"
            prefix="fas"/>
          {ViewerCounter.valueRounder(viewers)}
        </Badge>
        {renderShowChatBadge()}
      </div>
    )
  }

  return (
    <div className="live-badges _d-flex _mb-5 _justify-content-between">
      {renderLeftBadges()}
      {renderRightBadges()}
    </div>
  )
}

export default LiveBadges
